/**
 * Extension of A11yToggle that adds specific behaviours for
 * 'Read more' style functionality
 */

import { A11yToggle } from './A11yToggle';
import { debounce } from 'lodash-es';

export class A11yReadMore extends A11yToggle {
  #targetElClone;
  #wrapperEl;

  /**
   * Constructor
   * @param { import("./A11yReadMore").A11yReadMoreOptions } options - Intialisation options
   */
  constructor(options) {
    // Pass in options (initialiseToggle method won't fire yet)
    super({
      openText: 'Read more',
      closeText: 'Read less',
      ...options,
      triggerClasses: [
        'a11y-read-more__trigger',
        'button',
        'button--narrow-pill',
      ],
      targetClasses: ['a11y-read-more__target'],
      targetActiveClasses: ['a11y-read-more__target--expanded'],
    });

    this.hiddenContextText = options.hiddenContextText;

    // Naturalheight must be at least threshold greater than maxHeight
    this.threshold = options.threshold || 300;

    this.maxHeight = options.maxHeight || 100;

    this.useInert = options.useInert || false;

    // We'll want a standard 'read more' onToggle hanging off the parent class,
    // followed by an optional callback
    this.onToggle = () => {
      this.setHeights();
      this.setInert();
      if (!this.currentState) {
        window.scrollTo(0, this.targetEl.offsetTop);
      }
      options.onToggle?.(this.currentState);
    };

    this.isSetup = false;
    this.setup();
  }

  /**
   * Add the proper markup, insert the trigger
   * and kick off a11ytoggle / a11yreadmore
   */
  setup() {
    // Prevent double setup
    if (this.isSetup) {
      return;
    }

    // Prevent miniscule reveals
    if (this.targetEl.clientHeight - this.maxHeight < this.threshold) {
      return;
    }

    // Store a reference to the passed 'targetEl'
    this.#targetElClone = this.targetEl.cloneNode(true);

    // Build an empty wrapping structure, and store a reference
    this.targetEl.insertAdjacentHTML(
      'afterend',
      `
      <div class="a11y-read-more">
        <div></div>
      </div>
      `,
    );
    this.#wrapperEl = this.targetEl.nextElementSibling.firstElementChild;

    // Place the old content in the new structure and update 'targetEl'
    this.targetEl.remove();
    this.#wrapperEl.appendChild(this.#targetElClone);

    this.targetEl = this.#wrapperEl;

    // Insert trigger above the inner wrapper
    const buttonEl = document.createElement('button');
    buttonEl.ariaExpanded = false;
    buttonEl.type = 'button';
    buttonEl.insertAdjacentHTML(
      'beforeend',
      `
      <span class="wrap"><span class="a11y-read-more__text">${this.openText}</span></span>`,
    );
    // Add context text inside
    if (this.hiddenContextText) {
      buttonEl.lastElementChild.insertAdjacentHTML(
        'beforeend',
        `<span class="a11y-read-more__context visually-hidden">${this.hiddenContextText}</span>`,
      );
    }
    this.targetEl.insertAdjacentElement('beforebegin', buttonEl);
    this.triggerEl = this.targetEl.parentElement.firstElementChild;
    this.textEl = this.triggerEl.querySelector('.a11y-read-more__text');

    this.setId();

    // Initialise parent class
    this.initialiseToggle();

    this.calculateHeightValues();
    this.setHeights();
    this.setInert();
    this.targetEl.addEventListener('focusin', () => {
      this.#targetFocusHandler();
    });

    // Use lodash debounce to control resize callback
    this.debouncedResizeHandler = debounce(() => {
      this.#resizeHandler();
    }, 1000);

    window.addEventListener('resize', this.debouncedResizeHandler);

    this.isSetup = true;
  }

  /**
   * Revert the DOM to the state before setup
   */
  destroy() {
    // Prevent double destroy
    if (!this.isSetup) {
      return;
    }
    this.targetEl.parentElement.insertAdjacentElement(
      'afterend',
      this.#targetElClone,
    );
    this.targetEl.parentElement.remove();
    this.targetEl = this.#targetElClone;
    // N.B. targetFocusHandler doesn't need to be removed as it was never bound to the clone!
    window.removeEventListener('resize', this.debouncedResizeHandler);
    this.isSetup = false;
  }

  /**
   * Handle what happens if focus enters target element
   * NOTE: When useInert is true this cannot be invoked (in modern browsers).
   */
  #targetFocusHandler() {
    // If the content is hidden then reveal
    if (!this.currentState) {
      // Some browsers will set a scroll position on targetEl to show the active element
      // We'll handle ensuring it's visible on the window level
      this.targetEl.scrollTop = 0;

      this.triggerEl.click();

      // Bypass animation for better UX
      this.bypassTransition(true);

      // Update window scroll to show the active element
      // The timeout here is to accommodate other libraries like Tippy
      setTimeout(() => {
        window.scrollBy(0, document.activeElement.getBoundingClientRect().top);
      }, 100);

      this.setHeights();
    }
  }

  /**
   * Handle what happens when the window is resized
   */
  #resizeHandler() {
    // Catch the handler as it can be in the middle of firing during destruction
    if (this.isSetup) {
      this.calculateHeightValues();
      this.setHeights();
    }
  }

  /**
   * Set ID attributes based on how many other read-more's are initialised
   */
  setId() {
    this.targetEl.id = `a11y-read-more-${
      document.querySelectorAll('.a11y-read-more').length - 1
    }`;
  }

  /**
   * Mechanism for turning transition property on and off
   * @param {Boolean} flag True to bypass, false to re-enable
   */
  bypassTransition(flag) {
    this.targetEl.style.transitionProperty = flag ? 'none' : 'height';
  }

  /**
   * Determine current natural height and duration of transition
   */
  calculateHeightValues() {
    this.naturalHeight =
      this.targetEl.firstElementChild.clientHeight +
      parseInt(window.getComputedStyle(this.targetEl, ':after').height, 10);

    // Set what should be the transition duation based on fixed speed (2k pps)
    this.targetEl.style.setProperty(
      '--transition-duration',
      `${this.naturalHeight * 0.0005}s`,
    );
  }

  /**
   * Set height based on current state
   */
  setHeights() {
    if (this.currentState) {
      this.targetEl.style.height = `${this.naturalHeight}px`;
    } else {
      this.targetEl.style.height = `${this.maxHeight}px`;
    }
    this.bypassTransition(false);
  }

  /**
   * Set interactivity of target on or off
   */
  setInert() {
    if (!this.useInert) return;
    if (this.currentState) {
      this.targetEl.inert = false;
    } else {
      this.targetEl.inert = true;
    }
  }
}
